/**
 * Defensive projection of the synced schema (via `.toJSON()`) into the
 * plain shared RoomState. The projection — not the live schema — is what
 * panels render and what window.__vtt exposes (phase1-plan §2).
 */

import {
  createRoomState,
  createSeat,
  createTokenStats,
  type Effect,
  type ExpiryPhase,
  type LogEntry,
  type LogEntryKind,
  type RoomState,
  type SeatRole,
  type StatKey,
  type TimelineEntry,
  type Token,
} from "@asarimitsuha/table-core";

interface RawSeat {
  id?: unknown;
  name?: unknown;
  role?: unknown;
  connected?: unknown;
  reconnecting?: unknown;
}

interface RawStat {
  current?: unknown;
  max?: unknown;
}

interface RawToken {
  id?: unknown;
  name?: unknown;
  portrait?: unknown;
  col?: unknown;
  row?: unknown;
  size?: unknown;
  ownerSeatId?: unknown;
  characterRef?: unknown;
  hp?: RawStat;
  shield?: RawStat;
  mana?: RawStat;
  rp?: RawStat;
}

interface RawTimelineEntry {
  id?: unknown;
  tokenId?: unknown;
  label?: unknown;
}

interface RawEffect {
  id?: unknown;
  name?: unknown;
  kind?: unknown;
  sourceTokenId?: unknown;
  targetTokenId?: unknown;
  appliedAtRound?: unknown;
  appliedAtIndex?: unknown;
  /** "untilRemoved" | "scheduled" — the wire flattens the Effect.expiry union. */
  expiryKind?: unknown;
  anchorTokenId?: unknown;
  phase?: unknown;
  roundsRemaining?: unknown;
}

interface RawLogEntry {
  id?: unknown;
  kind?: unknown;
  text?: unknown;
  round?: unknown;
}

interface RawTableState {
  code?: unknown;
  roomName?: unknown;
  round?: unknown;
  timelineBegun?: unknown;
  currentIndex?: unknown;
  mapImageUrl?: unknown;
  mapWidthCells?: unknown;
  mapHeightCells?: unknown;
  mapCellSizePx?: unknown;
  mapFeetPerCell?: unknown;
  seats?: Record<string, RawSeat>;
  tokens?: Record<string, RawToken>;
  entries?: RawTimelineEntry[];
  effects?: Record<string, RawEffect>;
  log?: RawLogEntry[];
}

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function num(v: unknown, fallback: number): number {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

/** Schema strings can't be null; "" means absent on the wire. */
function nullable(v: unknown): string | null {
  const s = str(v);
  return s === "" ? null : s;
}

const STAT_KEYS: readonly StatKey[] = ["hp", "shield", "mana", "rp"];

const EXPIRY_PHASES: readonly ExpiryPhase[] = [
  "startOfTurn",
  "endOfTurn",
  "endOfRound",
];

function expiryPhase(v: unknown): ExpiryPhase {
  return EXPIRY_PHASES.includes(v as ExpiryPhase) ? (v as ExpiryPhase) : "endOfRound";
}

const LOG_KINDS: readonly LogEntryKind[] = [
  "expiry",
  "removal",
  "retarget",
  "override",
  "seat_removed",
];

/** Unflatten the wire's expiryKind encoding back to the shared union. For
 *  untilRemoved the wire sends anchor/phase "" and roundsRemaining 0. */
function projectEffect(id: string, raw: RawEffect): Effect {
  return {
    id: str(raw.id) || id,
    name: str(raw.name),
    kind: raw.kind === "buff" ? "buff" : "debuff",
    sourceTokenId: nullable(raw.sourceTokenId),
    targetTokenId: str(raw.targetTokenId),
    appliedAt: {
      round: num(raw.appliedAtRound, 1),
      index: num(raw.appliedAtIndex, 0),
    },
    expiry:
      raw.expiryKind === "untilRemoved"
        ? "untilRemoved"
        : {
            anchorTokenId: str(raw.anchorTokenId),
            phase: expiryPhase(raw.phase),
            roundsRemaining: num(raw.roundsRemaining, 1),
          },
  };
}

export function projectRoomState(raw: unknown): RoomState {
  const data = (raw ?? {}) as RawTableState;
  const state = createRoomState(str(data.code), str(data.roomName));

  state.timeline.round = num(data.round, 1);
  state.map = {
    imageUrl: nullable(data.mapImageUrl),
    widthCells: num(data.mapWidthCells, state.map.widthCells),
    heightCells: num(data.mapHeightCells, state.map.heightCells),
    cellSizePx: num(data.mapCellSizePx, state.map.cellSizePx),
    feetPerCell: num(data.mapFeetPerCell, state.map.feetPerCell),
  };

  for (const [id, rawSeat] of Object.entries(data.seats ?? {})) {
    const role: SeatRole = rawSeat.role === "gm" ? "gm" : "player";
    const seat = createSeat(str(rawSeat.id) || id, str(rawSeat.name), role);
    seat.connected = rawSeat.connected === true;
    seat.reconnecting = rawSeat.reconnecting === true;
    state.seats[seat.id] = seat;
  }

  for (const [id, rawToken] of Object.entries(data.tokens ?? {})) {
    const token: Token = {
      id: str(rawToken.id) || id,
      name: str(rawToken.name),
      portrait: nullable(rawToken.portrait),
      col: num(rawToken.col, 0),
      row: num(rawToken.row, 0),
      size: num(rawToken.size, 1),
      ownerSeatId: nullable(rawToken.ownerSeatId),
      characterRef: nullable(rawToken.characterRef),
      stats: createTokenStats({ hp: 0, mana: 0, rp: 0 }),
    };
    for (const key of STAT_KEYS) {
      const rawStat = rawToken[key] ?? {};
      token.stats[key] = {
        current: num(rawStat.current, 0),
        max: num(rawStat.max, 0),
      };
    }
    state.tokens[token.id] = token;
  }

  state.timeline.currentIndex = num(data.currentIndex, 0);
  if (data.timelineBegun === true) state.timeline.begun = true;
  for (const rawEntry of data.entries ?? []) {
    const entry: TimelineEntry = {
      id: str(rawEntry.id),
      tokenId: nullable(rawEntry.tokenId),
      label: nullable(rawEntry.label),
    };
    state.timeline.entries.push(entry);
  }

  for (const [id, rawEffect] of Object.entries(data.effects ?? {})) {
    const effect = projectEffect(id, rawEffect);
    state.effects[effect.id] = effect;
  }

  for (const rawLine of data.log ?? []) {
    // Kinds are a closed set today; an unrecognized one still renders (the
    // mirror passes kinds through verbatim), so keep the line rather than
    // silently dropping history.
    const line: LogEntry = {
      id: str(rawLine.id),
      kind: LOG_KINDS.includes(rawLine.kind as LogEntryKind)
        ? (rawLine.kind as LogEntryKind)
        : (str(rawLine.kind) as LogEntryKind),
      text: str(rawLine.text),
      round: num(rawLine.round, 1),
    };
    state.log.push(line);
  }
  return state;
}
